import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/tokenStorage';
import AppFieldsEditor from './AppFieldsEditor';
import { PROFILE_SUBTABS } from './ProfileAccount';
import { visibleSections, SMOKE_FREE_STATEMENT } from '../../config/applicationFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHOTO_KEYS = ['photo'];
const PACKAGE_LABEL = { envoy: 'Gold Envoy $20', single: 'Single $190', couples: 'Couples $290' };

const authConfig = () => {
  const token = getToken();
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const DocGrid = ({ documents, emptyMsg, testid }) => (
  documents.length === 0
    ? <p style={{ fontSize: '13px', color: '#6b7280' }} data-testid={`${testid}-empty`}>{emptyMsg}</p>
    : (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }} data-testid={testid}>
        {documents.map(doc => (
          <a key={doc.doc_id} href={doc.url} target="_blank" rel="noreferrer"
            style={{ display: 'flex', gap: '8px', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 10px', textDecoration: 'none', color: '#1a2332' }}>
            {doc.resource_type === 'image'
              ? <img src={doc.url} alt={doc.label} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }} />
              : <div style={{ width: '60px', height: '60px', background: '#fee2e2', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '12px' }}>PDF</div>}
            <div>
              <div style={{ fontSize: '12px', fontWeight: 700 }}>{doc.label}</div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>{new Date(doc.uploaded_at).toLocaleDateString('en-AU')}</div>
            </div>
          </a>
        ))}
      </div>
    )
);

const Card = ({ title, children }) => (
  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
    <h4 style={{ margin: '0 0 14px', color: '#1a2332', fontSize: '15px' }}>{title}</h4>
    {children}
  </div>
);

// Admin view/edit of a member's single combined Profile/Account — same tabs the member sees.
const AdminMemberProfile = ({ member, onClose, onRefresh }) => {
  const [data, setData] = useState(null);
  const [subTab, setSubTab] = useState('identity');
  const [savingUser, setSavingUser] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/members/${member.user_id}/profile-account`, authConfig())
      .then(res => setData(res.data))
      .catch(() => setError('Could not load this member.'));
  }, [member.user_id]);

  const saveUserField = async (payload) => {
    setSavingUser(true);
    try {
      await axios.patch(`${API}/admin/members/${member.user_id}`, payload, authConfig());
      setData(d => ({ ...d, member: { ...d.member, ...payload } }));
      if (onRefresh) onRefresh();
    } catch { setError('Could not save that change.'); }
    setSavingUser(false);
  };

  const application = data?.application;
  const sectionByKey = Object.fromEntries(visibleSections(application).map(s => [s.key, s]));
  const docs = data?.documents || [];

  const appPanel = (key) => {
    if (!application) return <p style={{ fontSize: '13px', color: '#6b7280' }} data-testid="admin-no-application">No application on file for this member.</p>;
    const section = sectionByKey[key];
    if (!section) return null;
    return <AppFieldsEditor section={section} application={application}
      onSaved={(updated) => { setData(d => ({ ...d, application: updated })); if (onRefresh) onRefresh(); }}
      getAuthConfig={authConfig} endpoint={`/admin/members/${member.user_id}/application`} />;
  };

  const fieldStyle = { width: '100%', padding: '9px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const m = data?.member || {};
  const isPaid = (m.payment_status || 'unpaid') === 'paid';

  const panels = {
    identity: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <Card title="Member Identity">
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center', marginBottom: '16px' }}>
            {m.avatar_url
              ? <img src={m.avatar_url} alt="avatar" style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover' }} />
              : <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#6b7280' }}>{(m.name || '?')[0]}</div>}
            <div>
              <div style={{ fontWeight: 700 }}>{m.name}</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>{m.email}</div>
              {m.membership_number && <div style={{ fontSize: '12px', color: '#6b7280' }}>Member No. {m.membership_number}</div>}
            </div>
          </div>
          {[['display_name', 'Display Name'], ['bio', 'Short Bio']].map(([key, label]) => (
            <div key={key} style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '5px' }}>{label}</label>
              {key === 'bio'
                ? <textarea defaultValue={m.bio || ''} rows={3} style={{ ...fieldStyle, resize: 'vertical' }}
                    onBlur={(e) => { if ((m.bio || '') !== e.target.value) saveUserField({ bio: e.target.value }); }}
                    data-testid={`admin-member-${key}`} />
                : <input defaultValue={m.display_name || ''} style={fieldStyle}
                    onBlur={(e) => { if ((m.display_name || '') !== e.target.value) saveUserField({ display_name: e.target.value }); }}
                    data-testid={`admin-member-${key}`} />}
            </div>
          ))}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '5px' }}>Availability to Move In</label>
            <select defaultValue={m.availability || ''} style={fieldStyle}
              onChange={(e) => saveUserField({ availability: e.target.value })} data-testid="admin-member-availability">
              <option value="">Not specified</option>
              {['Immediately', '1 week', '2 weeks', '1 month', '2 months', '3+ months', 'Flexible'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          {savingUser && <p style={{ fontSize: '12px', color: '#6b7280', margin: '10px 0 0' }}>Saving…</p>}
        </Card>
        {appPanel('personal')}
        {sectionByKey.partner && appPanel('partner')}
      </div>
    ),
    photos: <Card title="Member Photos"><DocGrid documents={docs.filter(d => PHOTO_KEYS.includes(d.doc_type))} emptyMsg="No photos uploaded by this member yet." testid="admin-member-photos" /></Card>,
    id: <Card title="ID & Verification"><DocGrid documents={docs.filter(d => !PHOTO_KEYS.includes(d.doc_type))} emptyMsg="No ID documents uploaded by this member yet." testid="admin-member-ids" /></Card>,
    financial: appPanel('financial'),
    lifestyle: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '10px 14px', fontSize: '13px', color: '#1e40af', fontWeight: 600 }}>{SMOKE_FREE_STATEMENT}</div>
        {appPanel('lifestyle')}
      </div>
    ),
    household: appPanel('household'),
    health: appPanel('health'),
    safety: appPanel('safety'),
    preferences: appPanel('preferences'),
    compatibility: appPanel('compatibility'),
    items: appPanel('items'),
    membership: (
      <Card title="Membership & Payment">
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ padding: '3px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: 700, background: isPaid ? '#d1fae5' : '#fef3c7', color: isPaid ? '#059669' : '#92400e' }} data-testid="admin-member-payment-status">
            {isPaid ? 'PAID' : 'PENDING'}
          </span>
          {m.paid_package && <span style={{ fontSize: '13px' }}>{PACKAGE_LABEL[m.paid_package] || m.paid_package}</span>}
          {m.paid_at && <span style={{ fontSize: '12px', color: '#6b7280' }}>{new Date(m.paid_at).toLocaleDateString('en-AU')}{m.paid_amount != null && ` · $${Number(m.paid_amount).toFixed(2)}`}</span>}
        </div>
        <div style={{ fontSize: '13px', color: '#374151', marginBottom: '12px' }}>
          Membership class: <strong>{m.membership_class || 'standard'}</strong>{application?.membership_type ? ` · ${application.membership_type}` : ''}
        </div>
        <h5 style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 6px' }}>Payment History</h5>
        {(data?.payments || []).length === 0 ? <p style={{ fontSize: '13px', color: '#6b7280' }}>No payments on file.</p> : (
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }} data-testid="admin-member-payments">
            <tbody>
              {data.payments.map(p => (
                <tr key={p.session_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '6px 0' }}>{p.created_at ? new Date(p.created_at).toLocaleDateString('en-AU') : '—'}</td>
                  <td style={{ padding: '6px 0' }}>{PACKAGE_LABEL[p.package_id] || p.package_id}</td>
                  <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600 }}>${Number(p.amount || 0).toFixed(2)}</td>
                  <td style={{ padding: '6px 0', textTransform: 'capitalize' }}>{p.payment_status || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    ),
    settings: (
      <Card title="Notifications & Account">
        <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.9 }}>
          <div>Email notifications: <strong>{m.notification_prefs?.email === false ? 'Off' : 'On'}</strong></div>
          <div>SMS notifications: <strong>{m.notification_prefs?.sms ? 'On' : 'Off'}</strong></div>
          <div>In-app notifications: <strong>{m.notification_prefs?.push === false ? 'Off' : 'On'}</strong></div>
          <div>Account status: <strong>{m.active === false ? 'Deactivated' : 'Active'}</strong></div>
          <div>Joined: <strong>{m.created_at ? new Date(m.created_at).toLocaleDateString('en-AU') : '—'}</strong></div>
        </div>
        <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: 0 }}>Passwords can only be changed by the member, or reset from the Members list.</p>
      </Card>
    ),
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={onClose} data-testid="admin-member-profile-modal">
      <div style={{ background: '#f8fafb', borderRadius: '14px', width: '100%', maxWidth: '900px', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #e5e7eb', background: 'white', borderRadius: '14px 14px 0 0' }}>
          <h3 style={{ margin: 0, color: '#1a2332', fontSize: '17px' }}>Profile / Account — {member.name || member.email}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#6b7280' }} data-testid="close-admin-member-profile">&times;</button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '10px 24px', background: 'white', borderBottom: '1px solid #e5e7eb' }} data-testid="admin-profile-subtabs">
          {PROFILE_SUBTABS.map(({ key, label }) => (
            <button key={key} onClick={() => setSubTab(key)} data-testid={`admin-profile-subtab-${key}`}
              style={{ border: 'none', background: subTab === key ? '#1a2332' : '#f3f4f6', color: subTab === key ? 'white' : '#4b5563',
                fontWeight: subTab === key ? 700 : 500, fontSize: '12px', padding: '7px 12px', borderRadius: '999px', cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ padding: '20px 24px', overflowY: 'auto' }}>
          {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', color: '#b91c1c', marginBottom: '14px', fontSize: '13px' }}>{error}</div>}
          {!data ? <p style={{ fontSize: '14px', color: '#6b7280' }}>Loading member…</p> : (panels[subTab] || panels.identity)}
        </div>
      </div>
    </div>
  );
};

export default AdminMemberProfile;
